import { Column, RowAlignedItems } from '@components/LinearLayout';
import { P, PBold } from '@components/Typography';
import { scaleInType2, scaleOutType2 } from '@theme/animations';
import styled, { css } from 'styled-components';

import { EVENT_HOME_WIDTH } from '@utils/constants';
import { Title } from '@pages/EventHome/components/EventCoLocatedEventsSection/styles';

export const EventCustomersSectionRoot = styled(Column)<{
  isAboveLimit?: boolean;
  isTicketsSection?: boolean;
}>`
  width: min(${EVENT_HOME_WIDTH}, 100%);
  margin: auto;
  padding-bottom: ${({ isTicketsSection }) =>
    isTicketsSection ? '7.5rem' : '5rem'};
  padding-top: ${({ isTicketsSection }) =>
    isTicketsSection ? '7.5rem' : '5rem'};
  gap: 5rem;
  padding-left: 1rem;
  padding-right: 1rem;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: auto;
  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width1280}) {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width480}) {
    padding-top: 5rem;
    padding-bottom: 5rem;

    .react-multiple-carousel__arrow--left {
      left: 0 !important;
    }

    .react-multiple-carousel__arrow--right {
      right: 0 !important;
    }
  }

  .react-multi-carousel-list {
    min-height: 490px;
    justify-content: ${({ isAboveLimit }) =>
      isAboveLimit ? 'normal' : 'center'};
  }

  .react-multi-carousel-track {
    align-items: center;
  }

  .react-multiple-carousel__arrow {
    background-color: ${({ theme }) => theme.color.mobilityGrey};
    border-radius: 1rem;
  }

  .react-multiple-carousel__arrow--left {
    left: calc(33% + 1px);
    z-index: 1;
  }

  .react-multiple-carousel__arrow--right {
    right: calc(33% + 1px);
    z-index: 1;
  }
`;

export const BackgroundSectionWrapper = styled.section<{ bgImage: string }>`
  background-image: url(${({ bgImage }) => bgImage});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
`;

export const SectionDescriptionText = styled(P)`
  color: ${({ theme }) => theme.color.mobilityGrey};
  font-size: ${({ theme }) => theme.fontSize.size22};
  text-align: center;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width480}) {
    font-size: ${({ theme }) => theme.fontSize.size18};
  }
`;

export const DescriptionText = styled(P)<{ isSelected?: boolean }>`
  color: ${({ theme }) => theme.color.mobilityGrey};
  font-size: ${({ theme, isSelected }) =>
    isSelected ? theme.fontSize.size18 : theme.fontSize.size14};
  font-weight: ${({ theme, isSelected }) =>
    isSelected ? theme.fontWeight.weight400 : theme.fontWeight.weight900};
  line-height: ${({ theme, isSelected }) =>
    isSelected ? theme.lineHeight.height150 : theme.lineHeight.height125};
  text-align: center;
  overflow: scroll;
  max-height: ${({ isSelected }) => (isSelected ? ' 240px' : ' 200px')};

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width768}) {
    font-size: ${({ theme }) => theme.fontSize.size16};
    font-weight: ${({ theme }) => theme.fontWeight.weight400};
    line-height: ${({ theme }) => theme.lineHeight.height140};
  }
`;

export const DescriptionWhiteText = styled(P)`
  color: ${({ theme }) => theme.color.white};
  font-size: ${({ theme }) => theme.fontSize.size14};
  line-height: ${({ theme }) => theme.lineHeight.height130};
  text-align: center;
`;

export const UnderlineContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width480}) {
    justify-content: flex-start;
  }
`;

export const Underline = styled.div<{
  color?: string;
}>`
  width: 160px;
  height: 0.4rem;
  background-color: ${({ color, theme }) => color || theme.color.white};
`;

export const FullNameText = styled(PBold)<{
  fontSize?: string;
  lineHeight?: string;
  isSelected?: boolean;
}>`
  color: ${({ theme }) => theme.color.white};
  font-size: ${({ theme, fontSize, isSelected }) =>
    isSelected
      ? fontSize || theme.fontSize.size18
      : fontSize || theme.fontSize.size14};
  line-height: ${({ theme, lineHeight }) =>
    lineHeight || theme.lineHeight.height150};
  font-weight: ${({ theme }) => theme.fontWeight.weight900};
  text-align: center;

  @media only screen and (max-width: ${({ theme }) =>
    theme.breakpoint.width768}) {
    font-size: ${({ theme, fontSize }) => fontSize || theme.fontSize.size16}};
    line-height: ${({ theme, lineHeight }) =>
      lineHeight || theme.lineHeight.height150};
    font-weight: ${({ theme, fontSize }) => (fontSize ? theme.fontWeight.weight400 : theme.fontWeight.weight900)};
  }
`;

export const EventCustomerTitle = styled(Title)<{ isTicketsSection?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.size50};
  text-align: center;
  line-height: ${({ theme }) => theme.lineHeight.height115};
  color: ${({ theme }) => theme.color.mobilityGrey};
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: auto;
  text-transform: uppercase;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width480}) {
    font-size: ${({ theme }) => theme.fontSize.size32};
    padding: ${({ isTicketsSection }) => (isTicketsSection ? '0 2.5rem' : '0')};
    text-align: left;
  }
`;

export const CustomerItemWrapper = styled.div`
  margin: 0 1.5rem;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width480}) {
    margin: 0;
  }
`;

export const CustomerItemRoot = styled(Column)<{
  $isHovered?: boolean;
  $borderRadius?: string;
  $width?: string;
}>`
  background-color: ${({ theme }) => theme.color.white};
  border: 4px solid ${({ theme }) => theme.color.strokeGrey};
  border-radius: ${({ theme, $borderRadius }) =>
    $borderRadius ?? theme.borderRadius.radius10};

  width: ${({ $width }) => $width || '100%'};
  height: 100%;
  overflow: hidden;
  justify-content: space-between;
  ${({ $isHovered }) => $isHovered && `cursor:pointer;`}
  ${({ $isHovered }) =>
    $isHovered
      ? css`
          animation: ${scaleOutType2} 300ms ease-in forwards;
        `
      : css`
          animation: ${scaleInType2} 300ms ease-in forwards;
        `}
`;

export const TopBodyRoot = styled.div<{
  color?: string;
}>`
  display: grid;
  grid-template-rows: 1fr auto auto;
  width: 100%;
  height: 100%;
`;

export const FooterCard = styled.div`
  display: grid;
  place-items: center;
  gap: 0.5rem;
  background-color: ${({ theme }) => theme.color.mobilityGrey};
  padding: 1.5rem 1.5rem;
  min-height: 11.8rem;
`;

export const CustomerItemsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width1080}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoint.width768}) {
    display: flex;
    flex-direction: column;
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 4rem;
`;

export const LogoWrapper = styled(RowAlignedItems)<{ borderColor?: string }>`
  border-bottom: 8px solid
    ${({ borderColor, theme }) => borderColor || theme.color.lightGrey2};
  justify-content: center;
  padding: 1rem 3rem;
`;

export const CompanyLogo = styled.img`
  height: 100px;
  width: auto;
  object-fit: cover;
`;

export const NoTestimonials = styled.p`
  font-size: ${({ theme }) => theme.fontSize.size20};
  margin: 3rem;
  text-align: center;
  widht: 100%;
`;
